import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import messages from './messages';

import { getStyles } from './styles';
import { Box, Button, CircularProgress, IconButton, Switch, Typography } from '@mui/material';
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../../utils/globalIcons';
import { useAppContext } from '../../../../context/AppContext';
import LoadBar from '../../../../utils/LoadBar';
import { tostAlert } from '../../../../utils/AlertToast';
import { Refresh } from '@mui/icons-material';

function ChatbotDataset({ action_type, dataLoading, datasets, getDatasets, datasetLoading, onSave, onCancel, onNext }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const intl = useIntl();
    const { setAppDetails, appDetails } = useAppContext();
    const [activeDataset, setActiveDataset] = useState([]);

    useEffect(() => {
        const filteredDatasets = (datasets || []).filter(item => {
            const data_sources = item?.payload?.source_types || [];
            const skipSources = ["mongodb", "ameya_extract", "food_recall", "appflyte"];
            const hasSkipSource = data_sources.some(source => skipSources.includes(source));
            if (hasSkipSource) {
                return true;
            } else {
                return item?.payload?.status === "Success";
            }
        }) ?? [];
        setActiveDataset(filteredDatasets);
    }, [datasets]);

    const handleToggle = (datasetObj) => {
        setAppDetails((prev) => {
            const app_bot_type = appDetails?.app_bot_type || 'simple_bot';
            const exists = prev.dataset.some(d => d?.payload?.dataset_id === datasetObj?.payload?.dataset_id);
            const hasExcelSelected = prev.dataset.some(d => d?.payload?.excel_analytics_status);
            const hasFoodRecallSelected = prev.dataset.some(d => d?.payload?.source_types?.includes("food_recall"));
            const hasMongoOrAmeya = prev.dataset.some(d => d?.payload?.source_types?.some(type => ["mongodb", "ameya_extract"].includes(type)));
            const hasAppflyte = prev.dataset.some(d => d?.payload?.source_types?.some(type => ["appflyte"].includes(type)));

            let updatedDataset = [...prev.dataset];
            let defaultDataset = prev.default_dataset;

            const sourceTypes = datasetObj?.payload?.source_types || [];
            const isMongoOrAmeya = sourceTypes.some(type => ["mongodb", "ameya_extract"].includes(type));
            const isAppflyte = sourceTypes.some(type => ["appflyte"].includes(type));

            if (isMongoOrAmeya) {
                if (exists) {
                    updatedDataset = [];
                } else {
                    updatedDataset = [datasetObj];
                    defaultDataset = datasetObj;
                }
                return {
                    ...prev,
                    dataset: updatedDataset,
                    default_dataset: defaultDataset
                };
            }

            if (hasMongoOrAmeya && !isMongoOrAmeya) {
                tostAlert(intl.formatMessage(messages.mongo_check), "warning");
                return prev;
            }

            if (isAppflyte) {
                if (exists) {
                    updatedDataset = [];
                } else {
                    updatedDataset = [datasetObj];
                    defaultDataset = datasetObj;
                }
                return {
                    ...prev,
                    dataset: updatedDataset,
                    default_dataset: defaultDataset
                };
            }

            if (hasAppflyte && !isAppflyte) {
                tostAlert(intl.formatMessage(messages.appflyte_check), "warning");
                return prev;
            }

            if (app_bot_type === 'simple_bot') {
                if (exists) {
                    updatedDataset = [];
                } else {
                    updatedDataset = [datasetObj];
                    defaultDataset = datasetObj;
                }
            } else if (datasetObj?.payload?.excel_analytics_status) {
                if (hasFoodRecallSelected) {
                    tostAlert(intl.formatMessage(messages.excel_check), "warning");
                    return prev;
                }
                const filtered = prev.dataset.filter(d =>
                    d?.payload?.excel_analytics_status &&
                    d?.payload?.dataset_id !== datasetObj?.payload?.dataset_id
                );
                updatedDataset = exists ? filtered : [...filtered, datasetObj];
            } else if (datasetObj?.payload?.source_types?.includes("food_recall")) {
                updatedDataset = exists ? [] : [datasetObj];
            } else if (hasExcelSelected || hasFoodRecallSelected) {
                tostAlert(intl.formatMessage(messages.excel_food_check), "warning");
                return prev;
            } else {
                const filtered = prev.dataset.filter(d =>
                    d?.payload?.dataset_id !== datasetObj?.payload?.dataset_id
                );
                updatedDataset = exists ? filtered : [...filtered, datasetObj];
            }

            return {
                ...prev,
                dataset: updatedDataset,
                default_dataset: defaultDataset
            };
        });
    };

    const handleNext = () => {
        if ((appDetails?.dataset || []).length === 0) {
            tostAlert(intl.formatMessage(messages.dataset_error), 'warning');
            return;
        }
        onNext('branding')
    }

    const checkDatasetType = (item) => {
        try {
            const { payload } = item || {};
            const sourceTypes = payload?.source_types || [];

            if (sourceTypes.includes("mongodb") || sourceTypes.includes("food_recall") || sourceTypes.includes("ameya_extract") || sourceTypes.includes("appflyte")) {
                return null;
            }

            return payload?.excel_analytics_status ? "excel" : "files";
        } catch (error) {
            console.error("Error in checkDatasetType:", error);
            return null;
        }
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>

                {(dataLoading || datasetLoading) && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>Select Datasets</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                            onClick={() => { action_type === "update" ? onSave('datasets') : handleNext() }}
                            disabled={dataLoading || datasetLoading}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                {action_type === "update" ? 'SAVE' : 'NEXT'}
                            </Typography>
                        </Button>
                        <Button
                            onClick={onCancel}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#404040' }}>
                                CANCEL
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box marginTop={'20px'} display={'flex'} justifyContent={'end'} alignItems={'center'}>
                    <IconButton
                        onClick={getDatasets}
                    >
                        <Refresh />
                    </IconButton>
                </Box>

                {(dataLoading || datasetLoading) ? (
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%" mt={10}>
                        <CircularProgress />
                    </Box>
                )
                    :
                    (<Box
                        sx={{
                            marginTop: '5px', display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: '40px',
                        }}
                    >
                        {activeDataset?.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" width="100%" mt={5}>
                                <Typography fontSize={14} fontWeight={400}>
                                    No Dataset Available
                                </Typography>
                            </Box>
                        ) : (
                            (activeDataset || []).map((item, index) => {
                                const datasetId = item?.payload?.dataset_id;
                                const isChecked = appDetails.dataset.some(
                                    (d) => d?.payload?.dataset_id === datasetId
                                );
                                const datasetName = item?.payload?.dataset_name || "";
                                const datasetType = checkDatasetType(item);

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            bgcolor: '#FFFFFF',
                                            borderRadius: '10px',
                                            border: '1px solid #DEDEDE',
                                            cursor: 'pointer',
                                            mb: 2,
                                        }}
                                    >
                                        <Box p={2.5}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography fontSize={15} fontWeight={600}>
                                                    {datasetName}
                                                    {datasetType ? ` (${datasetType})` : ""}
                                                </Typography>
                                                <Switch
                                                    disabled={dataLoading}
                                                    checked={isChecked}
                                                    onChange={() => handleToggle(item)}
                                                />
                                            </Box>

                                            <Box mt={1.5}>
                                                <Typography fontSize={11} fontWeight={600}>
                                                    SOURCES
                                                </Typography>
                                                <Box mt={1.2} display="flex" gap="8px" alignItems="center">
                                                    {(item?.payload?.source_types || []).map((source, idx) => {
                                                        const iconMap = {
                                                            google_drive: IconSvg.driveIcon,
                                                            one_drive: IconSvg.onedriveIcon,
                                                            aws_s3: IconSvg.awsIcon,
                                                            links: IconSvg.wwwIcon,
                                                            ameya_extract: IconSvg.ameyaIcon,
                                                            mongodb: IconSvg.mongoIcon,
                                                            gmail: IconSvg.mailIcon,
                                                            outlook: IconSvg.mailIcon,
                                                            files: IconSvg.fileSourceIcon,
                                                            food_recall: IconSvg.fastFoodIcon,
                                                            appflyte: IconSvg.apiIcon
                                                        };
                                                        return (
                                                            <ReactSVG
                                                                key={idx}
                                                                src={iconMap[source]}
                                                                beforeInjection={(svg) => {
                                                                    svg.setAttribute(
                                                                        'style',
                                                                        'width:18px; height:18px; display:block;'
                                                                    );
                                                                    if (source !== 'food_recall') {
                                                                        svg.setAttribute('stroke', '#000000');
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>)
                }
            </Box>
        </Box>
    )
}

export default ChatbotDataset;